/**
 * Native Cart Drawer
 * Replaces Monster Cart with native Shopify implementation
 * 
 * Note: Uses global functions from global.js:
 * - getCart(forceRefresh) - Cart fetching with caching
 * - invalidateCartCache() - Clear cart cache after updates
 * - debounce(fn, wait) - Debounce utility
 * - trapFocus(container) - Focus trap for accessibility
 * - removeTrapFocus(element) - Remove focus trap
 */

// Helper: Get section HTML (uses global parseHTML from global.js)
function getSectionInnerHTML(html, selector = '.shopify-section') {
  const doc = parseHTML(html);
  const section = doc.querySelector(selector);
  return section ? section.innerHTML : '';
}

function isFreeGiftLineItem(item) {
  if (!item?.properties) return false;
  const value = item.properties._free_gift;
  return value === 'true' || value === true;
}

function getGiftLineTier(item) {
  if (!isFreeGiftLineItem(item)) return null;
  const raw = item.properties._free_gift_tier;
  if (raw == null || raw === '') return null;
  const parsed = parseInt(String(raw), 10);
  return Number.isFinite(parsed) ? parsed : null;
}

function getGiftLineTierId(item) {
  if (!isFreeGiftLineItem(item)) return null;
  const raw = item.properties._free_gift_tier_id;
  if (raw == null || raw === '') return null;
  return String(raw);
}

function isExcludedFromGiftSubtotal(item) {
  if (!item) return true;
  if (isFreeGiftLineItem(item)) return true;
  const props = item.properties || {};
  if (props._exclude_gift_subtotal === 'true' || props._exclude_gift_subtotal === true) {
    return true;
  }
  return item.product_title === 'Shipping Protection';
}

/**
 * Main Cart Drawer Component
 */
class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.onDocumentKeydown = this.handleDocumentKeydown.bind(this);
    this.isUpdating = false;
    this.initialized = false;
    this._freeGiftAddInFlight = false;
    
    // Performance optimizations: State management
    this._renderPromise = null;
    this._updateQueue = [];
    this._abortController = null;
    this._sectionCache = null;
    this._cartStateCache = null;
    
    // DOM query cache
    this._cachedElements = {};
  }

  connectedCallback() {
    if (this.initialized) {
      console.log('Cart drawer: already initialized');
      return;
    }
    
    console.log('Cart drawer: connectedCallback called');
    
    this.drawer = this.querySelector('#cart-drawer') || document.getElementById('cart-drawer');
    this.toggle = document.getElementById('cart-toggle');
    this.overlay = document.getElementById('overlay');
    this.progressBar = this.querySelector('incentive-progress-bar');
    this.shippingProtection = this.querySelector('.cart-drawer__shipping-protection');
    this.sectionId = this.dataset.sectionId;

    this.init();
    this.initialized = true;
    document.addEventListener('keydown', this.onDocumentKeydown);

    // Check for #cart hash on initial load
    if (window.location.hash === '#cart') {
      // Wait a bit for everything to initialize
      setTimeout(() => {
        this.checkHashAndOpen();
      }, 300);
    }
  }

  disconnectedCallback() {
    // Clean up hashchange listener when component is removed
    if (this.hashChangeHandler) {
      window.removeEventListener('hashchange', this.hashChangeHandler);
    }
    document.removeEventListener('keydown', this.onDocumentKeydown);
    if (this.drawer && this._onDrawerKeyup) {
      this.drawer.removeEventListener('keyup', this._onDrawerKeyup);
    }
    if (this._closeButtons && this._onCloseClick) {
      this._closeButtons.forEach((closeButton) => {
        closeButton.removeEventListener('click', this._onCloseClick);
      });
    }
    if (this.overlay && this._onOverlayClick) {
      this.overlay.removeEventListener('click', this._onOverlayClick);
    }
    if (this.toggle && this.toggle._cartDrawerCleanup) {
      this.toggle._cartDrawerCleanup();
    }
    if (this._onCartUpdatedForGift) {
      document.removeEventListener('cart:updated', this._onCartUpdatedForGift);
      document.removeEventListener('cart:quantity_changed', this._onCartUpdatedForGift);
      document.removeEventListener('cart:item_removed', this._onCartUpdatedForGift);
    }
    if (this._onFreeGiftClick) {
      document.removeEventListener('click', this._onFreeGiftClick);
      this._freeGiftListenersAttached = false;
    }
  }

  init() {
    // Wait for DOM to be ready if elements aren't found yet
    if (!this.drawer) {
      // Try to find drawer by ID
      this.drawer = document.getElementById('cart-drawer');
    }

    if (!this.toggle) {
      this.toggle = document.getElementById('cart-toggle');
    }

    // If still no drawer or toggle, wait a bit and try again
    if (!this.drawer || !this.toggle) {
      console.warn('Cart drawer: drawer or toggle not found, retrying...', {
        drawer: !!this.drawer,
        toggle: !!this.toggle
      });
      setTimeout(() => {
        this.drawer = document.getElementById('cart-drawer');
        this.toggle = document.getElementById('cart-toggle');
        if (this.drawer && this.toggle) {
          this.init();
        } else {
          console.error('Cart drawer: Failed to initialize - elements not found');
        }
      }, 100);
      return;
    }

    // Bind listeners at most once. Element resolution above may retry, but the
    // listener-binding portion below must not run twice or handlers stack up.
    if (this._listenersBound) return;
    this._listenersBound = true;

    // Event listeners
    if (this.drawer) {
      this._onDrawerKeyup = (evt) => {
        if (evt.code === 'Escape') this.close();
      };
      this.drawer.addEventListener('keyup', this._onDrawerKeyup);
    }

    this._onCloseClick = () => this.close();
    this._closeButtons = this.querySelectorAll('.cart-drawer__close');
    this._closeButtons.forEach((closeButton) => {
      closeButton.addEventListener('click', this._onCloseClick);
    });

    if (this.toggle) {
      // Use instance-side bound flag rather than a DOM attribute so the listener
      // is correctly re-attached when <cart-drawer> is detached and re-rendered
      // (e.g. Shopify section reload). The previous pre-instance owns its
      // listener; clear it before binding the new one.
      if (this.toggle._cartDrawerCleanup) {
        this.toggle._cartDrawerCleanup();
      }
      console.log('Cart drawer: attaching click listener to toggle');
      this._onToggleClick = (e) => {
        console.log('Cart drawer: toggle clicked (from custom element)');
        e.preventDefault();
        e.stopPropagation();
        this.open();
      };
      this.toggle.addEventListener('click', this._onToggleClick);
      const toggleRef = this.toggle;
      const handlerRef = this._onToggleClick;
      this.toggle._cartDrawerCleanup = () => {
        toggleRef.removeEventListener('click', handlerRef);
        if (toggleRef._cartDrawerCleanup === this.toggle._cartDrawerCleanup) {
          delete toggleRef._cartDrawerCleanup;
        }
      };
    } else {
      console.warn('Cart drawer: toggle button not found');
    }

    if (this.overlay) {
      this._onOverlayClick = () => {
        // Free gift drawer owns the overlay while open — don't close cart underneath
        if (this.overlay?.classList.contains('free-gift-overlay')) {
          return;
        }
        if (this.overlay?.classList.contains('cart-overlay')) {
          this.close();
        }
      };
      this.overlay.addEventListener('click', this._onOverlayClick);
    }

    // Shipping protection toggle - attach after a short delay to ensure DOM is ready
    setTimeout(() => {
      this.attachShippingProtectionListener();
      this.attachCartPageShippingProtectionListener();
    }, 100);

    // Check for #cart hash in URL and open cart if present
    this.checkHashAndOpen();

    // Listen for hash changes (use bound method to maintain context)
    this.hashChangeHandler = () => {
      this.checkHashAndOpen();
    };
    window.addEventListener('hashchange', this.hashChangeHandler);

    // Upsell add to cart buttons
    this.querySelectorAll('.cart-drawer__upsell-add').forEach((button) => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        const variantId = button.dataset.variantId;
        if (variantId) {
          this.addUpsellToCart(variantId);
        }
      });
    });

    this.attachFreeGiftListeners();
    this._onCartUpdatedForGift = (event) => {
      if (event?.detail?.giftMaintenanceHandled) return;
      this.runGiftCartMaintenance();
    };
    document.addEventListener('cart:updated', this._onCartUpdatedForGift);
    document.addEventListener('cart:quantity_changed', this._onCartUpdatedForGift);
    document.addEventListener('cart:item_removed', this._onCartUpdatedForGift);
    this.runGiftCartMaintenance();
  }

  checkHashAndOpen() {
    if (window.location.hash === '#cart') {
      // Only open if cart is not already open to prevent infinite loops
      if (!this.drawer || !this.drawer.classList.contains('active')) {
        // Small delay to ensure drawer is fully initialized
        setTimeout(() => {
          this.open();
        }, 100);
      }
    } else if (this.drawer && this.drawer.classList.contains('active')) {
      // If hash is removed and cart is open, close it
      this.close();
    }
  }

  async open(addedToBag = false) {
    if (!this.drawer) {
      console.error('Cart drawer: drawer element not found');
      return;
    }
    const wasActive = this.drawer.classList.contains('active');

    console.log('Cart drawer: opening', {
      drawer: !!this.drawer,
      hasActiveClass: wasActive,
      zIndex: this.drawer.style.zIndex,
      addedToBag
    });

    // Close nav drawer if open
    const nav = document.querySelector('nav-drawer');
    if (nav && nav.mobileCollapse) {
      nav.mobileCollapse();
    }

    // Calculate top position based on announcement bar
    const ab = document.querySelector('#announcement-bar');
    if (ab) {
      const abBounding = ab.getBoundingClientRect();
      if (abBounding.bottom < 0) {
        this.drawer.style.setProperty('top', '60px');
      } else {
        const calcTop = (ab.offsetHeight + 60) - window.scrollY;
        this.drawer.style.setProperty('top', calcTop + 'px');
      }
    }

    // Set hash to #cart before opening to maintain consistency
    if (window.location.hash !== '#cart') {
      // Temporarily remove hashchange listener to prevent triggering close
      if (this.hashChangeHandler) {
        window.removeEventListener('hashchange', this.hashChangeHandler);
      }
      window.location.hash = '#cart';
      // Re-add listener after a brief delay
      if (this.hashChangeHandler) {
        setTimeout(() => {
          window.addEventListener('hashchange', this.hashChangeHandler);
        }, 100);
      }
    }

    // Render section BEFORE opening drawer when item is added to prevent layout shift
    // This ensures incentive bar and footer are present before the slide animation
    if (addedToBag) {
      // Ensure drawer is hidden during render (it should be by default via CSS)
      // This prevents any flash of content before the animation
      this.drawer.style.visibility = 'hidden';
      
      try {
        await this.renderSection();
        
        // Initialize incentive bar if it was just added (but don't update yet - let it animate in)
        // Re-find progress bar in case it was just added
        if (!this.progressBar) {
          this.progressBar = this.querySelector('incentive-progress-bar');
        }
        if (this.progressBar && !this.progressBar.initialized && this.progressBar.init) {
          // Only initialize, don't update - update will happen after drawer opens
          this.progressBar.init();
          this.progressBar.initialized = true;
        }
      } catch (error) {
        console.error('Error rendering section:', error);
      }
      
      // Visibility will be set to 'visible' when we add the 'active' class below
    }

    // Show drawer after content is rendered
    this.drawer.classList.add('animate', 'active');
    this.drawer.style.zIndex = '999';
    this.drawer.style.visibility = 'visible';
    
    if (this.overlay) {
      this.overlay.classList.add('active', 'cart-overlay');
    }
    setAriaExpanded(this.toggle, true);

    if (!wasActive) {
      lockBodyScroll();
      document.body.addEventListener('click', this.onBodyClick);
    }

    // Apply focus trap once — whichever fires first wins, so we still trap when
    // there's no transition (reduced-motion) or the drawer was already open.
    let trapped = false;
    const applyTrap = () => {
      if (trapped) return;
      trapped = true;
      this.drawer.focus();
      if (typeof trapFocus === 'function') {
        trapFocus(this.drawer);
      }
    };
    this.drawer.addEventListener('transitionend', applyTrap, { once: true });
    requestAnimationFrame(applyTrap);
    
    console.log('Cart drawer: after adding active class', {
      hasActive: this.drawer.classList.contains('active'),
      zIndex: this.drawer.style.zIndex,
      visibility: this.drawer.style.visibility,
      transform: window.getComputedStyle(this.drawer).transform
    });
    
    // Ensure shipping protection listener is attached and items are hidden after drawer opens
    setTimeout(() => {
      try {
        this.hideShippingProtectionItems();
        this.attachShippingProtectionListener();
      } catch (error) {
        console.error('Error setting up shipping protection:', error);
      }
    }, 200);

    // Update progress bar and incentive message after drawer is visible
    // This allows the bar to animate into place after the cart opens
    // Re-find progress bar in case it was just added
    if (!this.progressBar) {
      this.progressBar = this.querySelector('incentive-progress-bar');
    }
    if (this.progressBar) {
      // Ensure it's initialized
      if (!this.progressBar.initialized && this.progressBar.init) {
        this.progressBar.init();
        this.progressBar.initialized = true;
      }
      // Update the progress bar - this will animate into place
      if (this.progressBar.update) {
        // Use requestAnimationFrame to ensure drawer is visible first, then update
        requestAnimationFrame(() => {
          setTimeout(() => {
            if (this.progressBar && this.progressBar.update) {
              this.progressBar.update().catch((error) => {
                console.error('Error updating progress bar:', error);
              });
            }
          }, 50); // Small delay to ensure drawer animation has started
        });
      }
    }

    // Incentive message is now always visible in the main .shipping-message element
    // No temporary notification needed

    // Dispatch event
    this.dispatchEvent(new CustomEvent('cart:opened', { bubbles: true }));
  }

  setActiveElement(element) {
    // Store the active element for focus restoration (for accessibility)
    this.activeElement = element;
  }

  close() {
    if (!this.drawer) return;
    const wasActive = this.drawer.classList.contains('active');

    const freeGiftDrawer = document.querySelector('free-gift-drawer');
    if (freeGiftDrawer?.isOpen) {
      freeGiftDrawer.classList.remove('active');
      freeGiftDrawer.drawer?.classList.remove('active');
      freeGiftDrawer.isOpen = false;
    }

    // Remove #cart hash when closing
    if (window.location.hash === '#cart') {
      // Temporarily remove hashchange listener to prevent triggering open
      if (this.hashChangeHandler) {
        window.removeEventListener('hashchange', this.hashChangeHandler);
      }
      // Use history API to remove hash without triggering navigation
      if (window.history.replaceState) {
        window.history.replaceState(null, null, window.location.pathname + window.location.search);
      } else {
        window.location.hash = '';
      }
      // Re-add listener after a brief delay
      if (this.hashChangeHandler) {
        setTimeout(() => {
          window.addEventListener('hashchange', this.hashChangeHandler);
        }, 100);
      }
    }

    this.drawer.classList.remove('active');
    this.drawer.style.zIndex = '';
    if (this.overlay) {
      // Add fading class to trigger CSS fade while keeping top:0
      this.overlay.classList.remove('active');
      this.overlay.classList.add('overlay-fading');
      setTimeout(() => {
        // Now remove all drawer-related classes
        this.overlay.classList.remove('cart-overlay', 'free-gift-overlay', 'overlay-fading');
      }, 300); // Match the overlay transition duration
    }
    setAriaExpanded(this.toggle, false);

    if (wasActive) {
      unlockBodyScroll();
    }
    document.body.removeEventListener('click', this.onBodyClick);

    if (this.toggle && typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.toggle);
    }

    // Restore focus to previously active element
    if (this.activeElement && typeof this.activeElement.focus === 'function') {
      this.activeElement.focus();
    }

    this.dispatchEvent(new CustomEvent('cart:closed', { bubbles: true }));
  }

  handleDocumentKeydown(evt) {
    if (evt.key !== 'Escape' && evt.code !== 'Escape') return;
    const giftDrawer = document.querySelector('free-gift-drawer');
    if (giftDrawer?.isOpen) {
      evt.preventDefault();
      giftDrawer.close();
      return;
    }
    if (!this.drawer || !this.drawer.classList.contains('active')) return;
    evt.preventDefault();
    this.close();
  }

  handleBodyClick(evt) {
    const target = evt.target;
    const isOverlay = target.id === 'overlay' || target.closest('#overlay');
    
    // Skip if clicking overlay - let the overlay click handler handle it
    if (isOverlay) {
      return;
    }
    
    if (
      target !== this.drawer &&
      !target.closest('cart-drawer') &&
      !target.closest('free-gift-drawer')
    ) {
      const disclosure = target.closest('details-disclosure');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  /**
   * Get cached DOM element or query and cache it
   */
  _getCachedElement(selector, force = false) {
    if (!force && this._cachedElements[selector]) {
      const cached = this._cachedElements[selector];
      // Verify element still exists in DOM
      if (cached && document.contains(cached)) {
        return cached;
      }
    }
    const element = this.querySelector(selector);
    if (element) {
      this._cachedElements[selector] = element;
    }
    return element;
  }

  /**
   * Clear DOM cache (call after DOM updates)
   */
  _clearCache() {
    this._cachedElements = {};
  }

  /**
   * Render cart drawer section via AJAX with request deduplication
   */
  async renderSection() {
    if (!this.sectionId) return;
    
    // Request deduplication: if already rendering, return existing promise
    if (this._renderPromise) {
      return this._renderPromise;
    }
    
    if (this.isUpdating) {
      // Queue this request if one is already in progress
      return new Promise((resolve) => {
        this._updateQueue.push(() => this.renderSection().then(resolve));
      });
    }

    this.isUpdating = true;
    
    // Cancel any previous request
    if (this._abortController) {
      this._abortController.abort();
    }
    this._abortController = new AbortController();
    
    // Create promise for deduplication
    this._renderPromise = this._doRenderSection();
    
    try {
      return await this._renderPromise;
    } finally {
      this._renderPromise = null;
      this.isUpdating = false;
      
      // Process queued updates
      if (this._updateQueue.length > 0) {
        const nextUpdate = this._updateQueue.shift();
        if (nextUpdate) {
          // Use requestIdleCallback for non-critical updates
          if (window.requestIdleCallback) {
            requestIdleCallback(() => nextUpdate(), { timeout: 1000 });
          } else {
            setTimeout(nextUpdate, 0);
          }
        }
      }
    }
  }

  /**
   * Internal render method
   */
  async _doRenderSection() {
    try {
      const response = await fetch(`/?section_id=${this.sectionId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        signal: this._abortController.signal
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch section: ${response.status}`);
      }

      const html = await response.text();
      const newHTML = getSectionInnerHTML(html, `#shopify-section-${this.sectionId}`);

      if (newHTML) {
        // Cache section HTML for potential reuse
        this._sectionCache = newHTML;
        
        // Batch DOM updates using DocumentFragment where possible
        const newDoc = parseHTML(newHTML);
        
        // Update incentive bar - preserve existing element to prevent fade in/out
        const newIncentiveBar = newDoc.querySelector('incentive-progress-bar');

        if (newIncentiveBar) {
          const header = this._getCachedElement('.cart-drawer__header');
          if (header) {
            const allBars = Array.from(this.querySelectorAll('incentive-progress-bar'));
            allBars.slice(1).forEach((bar) => bar.remove());

            let incentiveBar = allBars[0] || null;

            if (incentiveBar) {
              if (newIncentiveBar.dataset.country !== undefined) {
                incentiveBar.dataset.country = newIncentiveBar.dataset.country;
              }

              const newIncentives = newIncentiveBar.dataset.incentives;
              if (newIncentives && incentiveBar.dataset.incentives !== newIncentives) {
                incentiveBar.dataset.incentives = newIncentives;
                incentiveBar.initialized = false;
                incentiveBar.init();
              }

              this.progressBar = incentiveBar;
            } else {
              header.insertAdjacentHTML('afterend', newIncentiveBar.outerHTML);
              this.progressBar = this.querySelector('incentive-progress-bar');
            }
          }
        } else {
          this.querySelectorAll('incentive-progress-bar').forEach((bar) => bar.remove());
          this.progressBar = null;
        }

        this.patchFreeGiftUI(newDoc);

        // Update cart items
        const itemsContainer = this._getCachedElement('#cart-drawer-items');
        const newItemsContainer = newDoc.querySelector('#cart-drawer-items');

        if (itemsContainer && newItemsContainer) {
          itemsContainer.innerHTML = newItemsContainer.innerHTML;
          
          // Hide shipping protection items if they somehow got rendered
          this.hideShippingProtectionItems();
        }

        // Update recommendations - preserve visible state to prevent fade in/out
        const recommendationsContainer = this._getCachedElement('.cart-drawer__recommendations');
        const newRecommendationsContainer = newDoc.querySelector('.cart-drawer__recommendations');
        
        if (newRecommendationsContainer) {
          if (recommendationsContainer) {
            // Check if recommendations are already visible with content - if so, skip update to prevent fade
            const isVisible = recommendationsContainer.classList.contains('visible');
            // Check for recommendation items (native carousel or legacy splide)
            const hasContent = recommendationsContainer.querySelector('.cart-drawer__recommendation-item') || 
                              recommendationsContainer.querySelector('.native-carousel__slide:not(.cart-drawer__recommendations-loading)');
            // Also check the data attribute that marks content as preserved
            const markedAsPreserved = recommendationsContainer.dataset.hasContent === 'true';
            
            // If recommendations are visible with content, don't replace them
            // This prevents the panel from fading in/out when cart updates or items are removed
            // Note: We no longer require isCartOpen because the active class may not be set yet during renderSection
            if ((isVisible && hasContent) || markedAsPreserved) {
              // Don't update recommendations container - keep existing one
              // This is critical to prevent items from vanishing when cart items are removed
            } else {
              // Only update if recommendations aren't already visible
              // Preserve visible class and updating state to prevent fade animations
              const wasVisible = recommendationsContainer.classList.contains('visible');
              const wasUpdating = recommendationsContainer.classList.contains('updating');
              
              // Replace container but preserve state
              recommendationsContainer.outerHTML = newRecommendationsContainer.outerHTML;
              
              // Re-find the container after replacement
              const updatedContainer = this._getCachedElement('.cart-drawer__recommendations', true);
              if (updatedContainer) {
                // Restore visible state if it was visible before
                if (wasVisible) {
                  updatedContainer.classList.add('visible');
                }
                // Restore updating state if it was updating
                if (wasUpdating) {
                  updatedContainer.classList.add('updating');
                }
              }
            }
          } else {
            // Insert recommendations after items container
            const contentWrapper = this._getCachedElement('.cart-drawer__content-wrapper');
            if (contentWrapper) {
              contentWrapper.insertAdjacentHTML('beforeend', newRecommendationsContainer.outerHTML);
            }
          }
        } else if (recommendationsContainer) {
          // Server didn't return recommendations - check if we should preserve existing ones
          // Check for recommendation items (native carousel or legacy)
          const hasValidItems = recommendationsContainer.querySelector('.cart-drawer__recommendation-item') || 
                                recommendationsContainer.querySelector('.native-carousel__slide:not(.cart-drawer__recommendations-loading)');
          const markedAsPreserved = recommendationsContainer.dataset.hasContent === 'true';
          const isVisible = recommendationsContainer.classList.contains('visible');
          const isCartOpen = this.classList.contains('active');
          
          // Don't remove if recommendations are visible and cart is open - they should stay static
          // This prevents them from disappearing when items are removed from cart
          if (markedAsPreserved || (isVisible && hasValidItems && isCartOpen)) {
            console.log('Preserving visible recommendations - server did not return them but cart still has items', {
              markedAsPreserved,
              isVisible,
              hasValidItems: !!hasValidItems,
              isCartOpen
            });
            // Keep the existing recommendations container - don't remove it
          } else if (!hasValidItems && (!isVisible || !isCartOpen)) {
            // Only remove if cart is empty (no valid items) AND recommendations are not visible
            recommendationsContainer.remove();
          }
        }

        // Update upsells
        const upsellsContainer = this._getCachedElement('.cart-drawer__upsells');
        const newUpsellsContainer = newDoc.querySelector('.cart-drawer__upsells');
        
        if (newUpsellsContainer) {
          if (upsellsContainer) {
            upsellsContainer.outerHTML = newUpsellsContainer.outerHTML;
          } else {
            // Insert upsells after recommendations
            const recommendations = this._getCachedElement('.cart-drawer__recommendations');
            if (recommendations) {
              recommendations.insertAdjacentHTML('afterend', newUpsellsContainer.outerHTML);
            } else {
              const contentWrapper = this._getCachedElement('.cart-drawer__content-wrapper');
              if (contentWrapper) {
                contentWrapper.insertAdjacentHTML('afterend', newUpsellsContainer.outerHTML);
              }
            }
          }
        } else if (upsellsContainer) {
          upsellsContainer.remove();
        }

        // Update totals and footer (including shipping protection)
        const totalsContainer = this._getCachedElement('.cart-drawer__links');
        const newTotalsContainer = newDoc.querySelector('.cart-drawer__links');

        if (newTotalsContainer) {
          // Footer exists in new HTML - add or update it
          if (totalsContainer) {
            // Update existing footer
            totalsContainer.innerHTML = newTotalsContainer.innerHTML;
          } else {
            // Create new footer - insert after upsells, recommendations, or content wrapper
            const upsells = this._getCachedElement('.cart-drawer__upsells');
            const recommendations = this._getCachedElement('.cart-drawer__recommendations');
            const contentWrapper = this._getCachedElement('.cart-drawer__content-wrapper');
            if (upsells) {
              upsells.insertAdjacentHTML('afterend', newTotalsContainer.outerHTML);
            } else if (recommendations) {
              recommendations.insertAdjacentHTML('afterend', newTotalsContainer.outerHTML);
            } else if (contentWrapper) {
              contentWrapper.insertAdjacentHTML('afterend', newTotalsContainer.outerHTML);
            } else {
              // Fallback: insert before closing cart-drawer div
              const drawer = this._getCachedElement('#cart-drawer');
              if (drawer) {
                const itemsContainer = drawer.querySelector('#cart-drawer-items');
                if (itemsContainer && itemsContainer.parentElement) {
                  itemsContainer.parentElement.insertAdjacentHTML('afterend', newTotalsContainer.outerHTML);
                } else {
                  drawer.insertAdjacentHTML('beforeend', newTotalsContainer.outerHTML);
                }
              }
            }
          }
          
          // Re-find shipping protection element after update
          const updatedTotalsContainer = this._getCachedElement('.cart-drawer__links', true);
          this.shippingProtection = updatedTotalsContainer ? updatedTotalsContainer.querySelector('.cart-drawer__shipping-protection') : null;
          
          // Re-attach event listener and hide shipping protection items
          if (this.shippingProtection) {
            this.hideShippingProtectionItems();
            this.attachShippingProtectionListener();
          }
        } else if (totalsContainer) {
          // Footer doesn't exist in new HTML but exists in DOM - remove it
          totalsContainer.remove();
          this.shippingProtection = null;
        }

        // Clear cache after DOM updates
        this._clearCache();
        
        this.attachFreeGiftListeners();

        // Re-attach upsell button listeners (after all DOM updates)
        this.querySelectorAll('.cart-drawer__upsell-add').forEach((button) => {
          // Remove existing listeners by cloning
          const newButton = button.cloneNode(true);
          button.parentNode.replaceChild(newButton, button);
          // Add new listener
          newButton.addEventListener('click', (e) => {
            e.preventDefault();
            const variantId = newButton.dataset.variantId;
            if (variantId) {
              this.addUpsellToCart(variantId);
            }
          });
        });

        // Re-initialize steppers and remove buttons
        this.querySelectorAll('ui-stepper').forEach((stepper) => {
          if (!stepper.initialized) {
            new Stepper().connectedCallback.call(stepper);
            stepper.initialized = true;
          }
        });

        this.querySelectorAll('remove-button').forEach((button) => {
          if (!button.initialized) {
            new RemoveButton().connectedCallback.call(button);
            button.initialized = true;
          }
        });

        // Update progress bar - ensure it's initialized first
        if (this.progressBar) {
          // Initialize if not already done
          if (!this.progressBar.initialized && this.progressBar.init) {
            this.progressBar.init();
            this.progressBar.initialized = true;
          }
          // Update the progress bar
          if (this.progressBar.update) {
            this.progressBar.update();
          }
        }

        // Update cart icon (batch with renderSection if possible)
        this.updateCartIcon();

        // Trigger cart updated event for recommendations
        this.dispatchEvent(new CustomEvent('cart:updated', {
          bubbles: true
        }));
        
        // Initialize cart recommendations after DOM update
        // This fixes the issue where inline scripts don't execute when DOM is replaced via innerHTML/outerHTML
        if (typeof window.initCartRecommendations === 'function') {
          window.initCartRecommendations();
        }
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Cart drawer render cancelled');
        return;
      }
      console.error('Error rendering cart drawer:', error);
      // Dispatch error event for error boundaries
      this.dispatchEvent(new CustomEvent('cart:error', {
        detail: { error },
        bubbles: true
      }));
    }
  }

  async updateCartIcon() {
    // Use AbortController for cancelable requests
    if (this._iconAbortController) {
      this._iconAbortController.abort();
    }
    this._iconAbortController = new AbortController();
    
    try {
      const response = await fetch('/?section_id=header', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        signal: this._iconAbortController.signal
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch header: ${response.status}`);
      }
      
      const html = await response.text();
      const iconContainer = parseHTML(html).getElementById('icon-cart');

      if (iconContainer) {
        const currentIcon = document.getElementById('icon-cart');
        if (currentIcon) {
          currentIcon.innerHTML = iconContainer.innerHTML;
        }
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        return; // Silently ignore cancelled requests
      }
      console.error('Error updating cart icon:', error);
    }
  }

  hideShippingProtectionItems() {
    // Get shipping protection identifiers
    let variantId = '';
    let productTitle = '';
    
    if (this.shippingProtection) {
      variantId = this.shippingProtection.getAttribute('data-variant-id')?.trim() || '';
      productTitle = this.shippingProtection.getAttribute('data-product-title')?.trim() || '';
    }
    
    if (!variantId && !productTitle) return;
    
    // Find and hide shipping protection line items by matching product title
    const lineItems = this.querySelectorAll('.cart__line-item');
    lineItems.forEach((item) => {
      const itemProductTitle = item.querySelector('.name')?.textContent?.trim() || 
                              item.querySelector('.cart__line-item--title-group .name')?.textContent?.trim() || 
                              item.querySelector('a.name')?.textContent?.trim() || '';
      
      if (productTitle && itemProductTitle.toLowerCase() === productTitle.toLowerCase()) {
        item.style.display = 'none';
        item.classList.add('shipping-protection-item');
      }
    });
  }

  attachShippingProtectionListener() {
    // Find shipping protection element (it might have been re-rendered)
    this.shippingProtection = this.querySelector('.cart-drawer__shipping-protection');
    
    if (!this.shippingProtection) {
      return;
    }
    
    const toggle = this.shippingProtection.querySelector('#shipping-protection-toggle');
    
    if (!toggle) {
      console.warn('Shipping protection toggle input not found');
      return;
    }
    
    // Remove any existing listeners by cloning the element
    const newToggle = toggle.cloneNode(true);
    toggle.parentNode.replaceChild(newToggle, toggle);
    
    // Add change listener to checkbox
    newToggle.addEventListener('change', (e) => {
      this.handleShippingProtectionToggle(e.target.checked);
    });
    
    // Also add click listener as backup to ensure it works
    newToggle.addEventListener('click', (e) => {
      // Let the default behavior happen, which will trigger the change event
    });
  }

  attachCartPageShippingProtectionListener() {
    const container = document.querySelector('.cart-page__summary .cart-drawer__shipping-protection');
    if (!container) return;

    const toggle = container.querySelector('.toggle-switch__input') || container.querySelector('input[type="checkbox"]');
    if (!toggle) return;

    const newToggle = toggle.cloneNode(true);
    toggle.parentNode.replaceChild(newToggle, toggle);

    newToggle.addEventListener('change', (e) => {
      this.handleShippingProtectionToggle(e.target.checked, container);
    });
  }

  setToggleLoading(loading, element) {
    const el = element ?? this.shippingProtection;
    if (!el) return;
    
    const toggleSwitch = el.querySelector('.toggle-switch');
    const toggleInput = el.querySelector('.toggle-switch__input') || el.querySelector('#shipping-protection-toggle');
    
    if (toggleSwitch) {
      if (loading) {
        toggleSwitch.classList.add('loading');
        toggleSwitch.setAttribute('tabindex', '-1');
        toggleSwitch.setAttribute('aria-disabled', 'true');
      } else {
        toggleSwitch.classList.remove('loading');
        toggleSwitch.setAttribute('tabindex', '0');
        toggleSwitch.setAttribute('aria-disabled', 'false');
      }
    }
    
    if (toggleInput) {
      toggleInput.disabled = loading;
    }
  }

  async handleShippingProtectionToggle(enabled, contextElement) {
    const ctx = contextElement ?? this.shippingProtection;
    if (!ctx) {
      console.error('Shipping protection: element not found');
      return;
    }

    // Set loading state (use context element for cart page)
    this.setToggleLoading(true, ctx);

    // Get data attributes from context element
    const variantIdAttr = ctx.getAttribute('data-variant-id');
    const productTitleAttr = ctx.getAttribute('data-product-title');
    const variantId = (variantIdAttr || '').trim();
    const productTitle = (productTitleAttr || '').trim();

    console.log('Shipping protection toggle:', {
      enabled,
      variantId,
      productTitle,
      variantIdAttr,
      productTitleAttr,
      element: ctx
    });

    if (!variantId && !productTitle) {
      console.error('Shipping protection: variant ID or product title required.', {
        variantId,
        productTitle,
        element: ctx,
        html: ctx.outerHTML
      });
      const toggle = ctx.querySelector('.toggle-switch__input') || ctx.querySelector('#shipping-protection-toggle');
      if (toggle) toggle.checked = !enabled;
      alert('Shipping protection is not configured. Please set the shipping protection product in theme settings. Check console for details.');
      this.setToggleLoading(false, ctx);
      return;
    }

    try {
      // Check if protection is already in cart (force refresh to get latest)
      const cart = await getCart(true);
      let protectionItem = null;

      console.log('Checking cart for shipping protection:', {
        cartItems: cart.items,
        variantId,
        productTitle
      });

      for (const item of cart.items) {
        const itemVariantId = item.variant_id ? item.variant_id.toString() : '';
        const itemProductTitle = item.product_title || '';
        if ((variantId && itemVariantId === variantId.toString()) ||
            (productTitle && itemProductTitle.toLowerCase() === productTitle.toLowerCase())) {
          protectionItem = item;
          console.log('Found protection item:', protectionItem);
          break;
        }
      }

      if (enabled && !protectionItem) {
        let variantIdToAdd = (variantId || '').trim();
        // Cart page section often has no shipping_protection_product set; use drawer's variant ID as fallback
        if (!variantIdToAdd && productTitle && ctx.closest('.cart-page__summary')) {
          const drawerBlock = this.querySelector('.cart-drawer__shipping-protection');
          const drawerVariantId = drawerBlock?.getAttribute('data-variant-id')?.trim();
          if (drawerVariantId) variantIdToAdd = drawerVariantId;
        }
        if (!variantIdToAdd) {
          console.error('Shipping protection: variant ID is required to add protection.', {
            variantId,
            productTitle,
            elementHTML: ctx.outerHTML.substring(0, 200)
          });
          const toggle = ctx.querySelector('.toggle-switch__input') || ctx.querySelector('#shipping-protection-toggle');
          if (toggle) toggle.checked = false;
          alert('Shipping protection variant ID is missing. Please ensure the shipping protection product is selected in theme settings and has variants available.');
          this.setToggleLoading(false, ctx);
          return;
        }
        try {
          console.log('Adding shipping protection with variant ID:', variantIdToAdd);
          await this.addShippingProtection(variantIdToAdd);
        } catch (error) {
          console.error('Failed to add shipping protection:', error);
          const toggle = ctx.querySelector('.toggle-switch__input') || ctx.querySelector('#shipping-protection-toggle');
          if (toggle) toggle.checked = false;
          alert('Failed to add shipping protection: ' + (error.message || 'Unknown error'));
          throw error;
        }
      } else if (!enabled && protectionItem) {
        try {
          const lineItemId = protectionItem.id || protectionItem.key;
          console.log('Removing shipping protection:', { protectionItem, lineItemId });
          if (!lineItemId) {
            throw new Error('Line item ID not found in cart item. Item: ' + JSON.stringify(protectionItem));
          }
          await this.removeShippingProtection(lineItemId);
          setTimeout(async () => {
            const updatedCart = await getCart(true);
            const stillInCart = updatedCart.items.find(item => {
              const itemVariantId = item.variant_id ? item.variant_id.toString() : '';
              const itemProductTitle = item.product_title || '';
              return (variantId && itemVariantId === variantId.toString()) ||
                     (productTitle && itemProductTitle.toLowerCase() === productTitle.toLowerCase());
            });
            if (stillInCart) console.warn('Shipping protection still in cart after removal attempt');
            else console.log('Shipping protection successfully removed from cart');
          }, 500);
        } catch (error) {
          console.error('Failed to remove shipping protection:', error);
          const toggle = ctx.querySelector('.toggle-switch__input') || ctx.querySelector('#shipping-protection-toggle');
          if (toggle) toggle.checked = true;
          alert('Failed to remove shipping protection: ' + (error.message || 'Unknown error'));
          throw error;
        }
      } else if (!enabled && !protectionItem) {
        console.log('Toggle is off but protection item not found in cart - already removed');
      } else if (enabled && protectionItem) {
        console.log('Toggle is on and protection item already in cart - no action needed');
      }

      this.dispatchEvent(new CustomEvent('cart:shipping_protection_toggled', { detail: { enabled } }));
      // Refresh cart page when toggled from cart page
      if (ctx.closest('.cart-page__summary')) {
        document.dispatchEvent(new CustomEvent('cart:updated'));
      }
    } catch (error) {
      console.error('Error toggling shipping protection:', error);
      const toggle = ctx.querySelector('.toggle-switch__input') || ctx.querySelector('#shipping-protection-toggle');
      if (toggle) toggle.checked = !enabled;
    } finally {
      this.setToggleLoading(false, ctx);
    }
  }

  async addShippingProtection(variantId) {
    if (!variantId) {
      throw new Error('Variant ID is required');
    }

    // Ensure variant ID is a number
    const variantIdNum = parseInt(variantId.toString().trim(), 10);
    if (isNaN(variantIdNum) || variantIdNum <= 0) {
      throw new Error('Invalid variant ID: ' + variantId);
    }

    console.log('Adding shipping protection variant:', variantIdNum);
    
    // Invalidate cache before adding
    invalidateCartCache();

    const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        id: variantIdNum,
        quantity: 1
      })
    });

    const responseData = await response.json();

    if (!response.ok) {
      console.error('Cart add error:', responseData);
      // Shopify cart API returns errors in description or message field
      throw new Error(responseData.description || responseData.message || 'Failed to add shipping protection');
    }

    console.log('Shipping protection added successfully:', responseData);

    // Update cart icon
    await this.updateCartIcon();
    
    // Re-render the section to update the toggle state
    await this.renderSection();
  }

  async removeShippingProtection(lineItemId) {
    console.log('removeShippingProtection called with lineItemId:', lineItemId);
    
    // Ensure lineItemId is a valid value
    if (!lineItemId) {
      throw new Error('Line item ID is required for removal');
    }
    
    // Invalidate cache before removing
    invalidateCartCache();
    
    const response = await fetch(window.Shopify.routes.root + 'cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        id: String(lineItemId),
        quantity: 0
      })
    });

    const responseData = await response.json();
    console.log('Remove shipping protection response:', {
      ok: response.ok,
      status: response.status,
      data: responseData
    });

    if (!response.ok) {
      console.error('Remove shipping protection error:', responseData);
      // Shopify cart API returns errors in description or message field
      throw new Error(responseData.description || responseData.message || 'Failed to remove shipping protection');
    }

    // Verify the item was removed
    if (responseData.items) {
      const stillInCart = responseData.items.find(item => {
        const itemId = item.id ? String(item.id) : '';
        return itemId === String(lineItemId);
      });
      
      if (stillInCart) {
        console.warn('Item still in cart after removal attempt');
      }
    }

    // Update cart icon
    await this.updateCartIcon();
    
    // Re-render the section to update the toggle state
    await this.renderSection();
  }

  attachFreeGiftListeners() {
    if (this._freeGiftListenersAttached) return;
    this._freeGiftListenersAttached = true;

    this._onFreeGiftClick = async (e) => {
      if (e.target.closest('[data-free-gift-notification-open]')) {
        e.preventDefault();
        document.querySelector('free-gift-drawer')?.open?.();
        return;
      }

      if (!e.target.closest('[data-free-gift-drawer]')) return;

      const addBtn = e.target.closest('[data-gift-add]');
      if (addBtn) {
        e.preventDefault();
        await this.handleGiftAddClick(addBtn);
      }
    };

    document.addEventListener('click', this._onFreeGiftClick);
  }

  patchFreeGiftUI(newDoc) {
    const newIncentiveBar = newDoc?.querySelector('incentive-progress-bar');

    document.querySelectorAll('incentive-progress-bar').forEach((bar) => {
      if (newIncentiveBar) {
        bar.classList.toggle(
          'incentive-progress-bar--gift-pending',
          newIncentiveBar.classList.contains('incentive-progress-bar--gift-pending')
        );
      } else {
        bar.classList.remove('incentive-progress-bar--gift-pending');
      }

      let giftSlot = bar.querySelector('.incentive-bar__gift-notification');
      const newGiftSlot = newIncentiveBar?.querySelector('.incentive-bar__gift-notification');

      if (newGiftSlot && newGiftSlot.innerHTML.trim() !== '') {
        if (!giftSlot) {
          giftSlot = document.createElement('div');
          giftSlot.className = 'incentive-bar__gift-notification';
          bar.appendChild(giftSlot);
        }
        giftSlot.innerHTML = newGiftSlot.innerHTML;
        giftSlot.hidden = false;
      } else if (giftSlot) {
        giftSlot.hidden = true;
        giftSlot.innerHTML = '';
        bar.classList.remove('incentive-progress-bar--gift-pending');
      }
    });

    const drawerGrid = document.querySelector('[data-free-gift-drawer-grid]');
    const newGrid = newDoc?.querySelector('[data-free-gift-drawer-grid]');
    if (drawerGrid && newGrid) {
      drawerGrid.innerHTML = newGrid.innerHTML;
    }
  }

  async handleGiftAddClick(button) {
    const handle = button.dataset.productHandle;
    const variantId = button.dataset.variantId;
    let variantsCount = parseInt(button.dataset.variantsCount, 10);

    if (!handle) return;

    if (!Number.isFinite(variantsCount) || variantsCount <= 0) {
      try {
        const productResponse = await fetch(`/products/${handle}.js`);
        if (productResponse.ok) {
          const productData = await productResponse.json();
          variantsCount = Array.isArray(productData?.variants) ? productData.variants.length : 0;
          button.dataset.variantsCount = String(variantsCount);
        }
      } catch (error) {
        console.error('Error loading gift product data:', error);
      }
    }

    if (variantsCount > 1 && window.QuickViewService?.open) {
      const tierThreshold = button.dataset.giftTierThreshold
        || document.querySelector('[data-free-gift-notification]')?.dataset?.threshold;
      const tierId = button.dataset.giftTierId
        || document.querySelector('[data-free-gift-notification]')?.dataset?.tierId;
      window.QuickViewService.open(handle, null, button, {
        freeGift: true,
        freeGiftTier: tierThreshold || undefined,
        freeGiftTierId: tierId || undefined
      });
      return;
    }

    await this.addFreeGift(variantId, button);
  }

  resolvePendingGiftTierThreshold(button = null) {
    const fromButton = button?.dataset?.giftTierThreshold;
    if (fromButton) {
      const parsed = parseInt(fromButton, 10);
      if (Number.isFinite(parsed)) return parsed;
    }

    const fromNotification = document.querySelector('[data-free-gift-notification]')?.dataset?.threshold;
    if (fromNotification) {
      const parsed = parseInt(fromNotification, 10);
      if (Number.isFinite(parsed)) return parsed;
    }

    return null;
  }

  resolvePendingGiftTierId(button = null) {
    const fromButton = button?.dataset?.giftTierId;
    if (fromButton) return String(fromButton);

    const fromNotification = document.querySelector('[data-free-gift-notification]')?.dataset?.tierId;
    if (fromNotification) return String(fromNotification);

    return null;
  }

  async addFreeGift(variantId, button = null) {
    if (!variantId) return;
    if (this._freeGiftAddInFlight) return;

    const tierThreshold = this.resolvePendingGiftTierThreshold(button);
    const tierId = this.resolvePendingGiftTierId(button);

    try {
      invalidateCartCache();
      const cart = await getCart(true);
      if (tierId != null) {
        const existingSameTier = (cart?.items || []).some(
          (item) =>
            isFreeGiftLineItem(item) && getGiftLineTierId(item) === tierId
        );
        if (existingSameTier) return;
      } else if (tierThreshold != null) {
        const existingSameTier = (cart?.items || []).some(
          (item) =>
            isFreeGiftLineItem(item) && getGiftLineTier(item) === tierThreshold
        );
        if (existingSameTier) return;
      }
    } catch {
      // proceed — cart/add will still enforce limits via maintenance
    }

    this._freeGiftAddInFlight = true;

    if (button) {
      button.disabled = true;
      button.classList.add('loading');
    }

    let giftAdded = false;

    try {
      invalidateCartCache();

      const properties = { _free_gift: 'true' };
      if (tierThreshold != null) {
        properties._free_gift_tier = String(tierThreshold);
      }
      if (tierId != null) {
        properties._free_gift_tier_id = String(tierId);
      }

      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: parseInt(variantId, 10),
          quantity: 1,
          properties
        })
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.description || responseData.message || 'Failed to add free gift');
      }

      invalidateCartCache();
      await this.renderSection();

      giftAdded = true;
    } catch (error) {
      console.error('Error adding free gift:', error);
      alert(error.message || 'Failed to add free gift');
    } finally {
      this._freeGiftAddInFlight = false;
      if (button) {
        button.disabled = false;
        button.classList.remove('loading');
      }
      if (giftAdded) {
        document.querySelector('free-gift-drawer')?.close?.();
      }
    }

    if (giftAdded && this.progressBar?.update) {
      this.progressBar.update();
    }
  }

  getQualifyingSubtotal(cart) {
    if (!cart?.items) return 0;

    let total = 0;

    cart.items.forEach((item) => {
      if (isExcludedFromGiftSubtotal(item)) return;
      total += item.original_line_price || (item.original_price * item.quantity);
    });

    return total;
  }

  getGiftTierIncentives(cart = null) {
    const bar = document.querySelector('incentive-progress-bar');
    if (!bar?.dataset.incentives) return [];

    try {
      const incentives = JSON.parse(bar.dataset.incentives);
      const currency = (
        cart?.currency ||
        window.Shopify?.currency?.active ||
        'AUD'
      ).toUpperCase();

      return incentives
        .filter((incentive) => {
          const label = (incentive.label || '').toLowerCase();
          return incentive.isGiftTier || label.includes('free gift') || incentive.icon === 'gift';
        })
        .map((incentive) => ({
          threshold: incentive.thresholds?.[currency] || 0
        }))
        .filter((tier) => tier.threshold > 0)
        .sort((a, b) => a.threshold - b.threshold);
    } catch (error) {
      console.error('Error parsing gift tier incentives:', error);
      return [];
    }
  }

  getEligibleGiftTierThresholds(cart) {
    const subtotal = this.getQualifyingSubtotal(cart);
    return this.getGiftTierIncentives(cart)
      .filter((tier) => subtotal >= tier.threshold)
      .map((tier) => tier.threshold);
  }

  getGiftCollectionTiers() {
    const script = document.querySelector('[data-free-gift-collection-tiers]');
    if (!script) return new Map();

    try {
      const parsed = JSON.parse(script.textContent || '[]');
      const map = new Map();
      if (Array.isArray(parsed)) {
        parsed.forEach((tier) => {
          if (!tier || !tier.id) return;
          const ids = Array.isArray(tier.productIds)
            ? new Set(tier.productIds.map((id) => String(id)))
            : new Set();
          map.set(String(tier.id), ids);
        });
      }
      return map;
    } catch (error) {
      console.error('Error parsing free gift collection tiers:', error);
      return new Map();
    }
  }

  getCartProductIds(cart) {
    const ids = new Set();
    if (!cart?.items) return ids;
    cart.items.forEach((item) => {
      if (isFreeGiftLineItem(item)) return;
      if (item?.product_id != null) ids.add(String(item.product_id));
    });
    return ids;
  }

  getEligibleGiftCount(cart) {
    return this.getEligibleGiftTierThresholds(cart).length;
  }

  getFreeGiftThreshold() {
    const notification = document.querySelector('[data-free-gift-notification]');
    if (notification?.dataset.threshold) {
      const parsed = parseInt(notification.dataset.threshold, 10);
      if (Number.isFinite(parsed)) return parsed;
    }

    const tiers = this.getGiftTierIncentives();
    if (tiers.length) return tiers[0].threshold;

    const drawer = document.querySelector('cart-drawer[data-free-gift-threshold]');
    if (drawer?.dataset.freeGiftThreshold) {
      const parsed = parseInt(drawer.dataset.freeGiftThreshold, 10);
      if (Number.isFinite(parsed)) return parsed;
    }
    return 15000;
  }

  getExcessGiftLines(cart) {
    if (!cart?.items) return [];

    const eligibleThresholds = new Set(this.getEligibleGiftTierThresholds(cart));
    const collectionTiers = this.getGiftCollectionTiers();
    const cartProductIds = this.getCartProductIds(cart);
    const giftLines = cart.items.filter(isFreeGiftLineItem);
    const excess = [];
    const legacy = [];
    const keptTierCounts = new Map();
    const keptCollectionTierIds = new Set();

    giftLines.forEach((item) => {
      if ((item.quantity || 1) > 1) {
        excess.push(item);
        return;
      }

      const collectionTierId = getGiftLineTierId(item);
      if (collectionTierId && collectionTiers.has(collectionTierId)) {
        const qualifyingIds = collectionTiers.get(collectionTierId);
        const satisfied = [...qualifyingIds].some((id) => cartProductIds.has(id));
        if (!satisfied || keptCollectionTierIds.has(collectionTierId)) {
          excess.push(item);
        } else {
          keptCollectionTierIds.add(collectionTierId);
        }
        return;
      }

      const tier = getGiftLineTier(item);
      if (tier != null) {
        if (!eligibleThresholds.has(tier)) {
          excess.push(item);
          return;
        }
        const kept = keptTierCounts.get(tier) || 0;
        if (kept >= 1) {
          excess.push(item);
        } else {
          keptTierCounts.set(tier, kept + 1);
        }
        return;
      }

      legacy.push(item);
    });

    const keptTaggedCount = [...keptTierCounts.values()].reduce(
      (sum, count) => sum + count,
      0
    );
    const legacyAllowed = Math.max(0, eligibleThresholds.size - keptTaggedCount);
    if (legacy.length > legacyAllowed) {
      excess.push(...legacy.slice(legacyAllowed));
    }

    return excess;
  }

  async removeGiftLines(linesToRemove) {
    if (!linesToRemove.length) return null;

    const updates = {};
    linesToRemove.forEach((item) => {
      const lineId = item.key || item.id;
      if (lineId) updates[lineId] = 0;
    });

    if (!Object.keys(updates).length) return null;

    invalidateCartCache();

    const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ updates })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.description || errorData.message || 'Failed to remove free gift');
    }

    return response.json();
  }

  async enforceGiftLimits(cart) {
    if (!cart?.items) return { cart, changed: false };

    const excess = this.getExcessGiftLines(cart);
    if (!excess.length) return { cart, changed: false };

    const updatedCart = await this.removeGiftLines(excess);
    return { cart: updatedCart || cart, changed: !!updatedCart };
  }

  async dedupeGiftLines(cart) {
    const { changed } = await this.enforceGiftLimits(cart);
    return changed;
  }

  async removeGiftsBelowThreshold(cart) {
    const { changed } = await this.enforceGiftLimits(cart);
    return changed;
  }

  async runGiftCartMaintenance() {
    if (this._giftMaintenanceRunning) return;
    this._giftMaintenanceRunning = true;

    try {
      invalidateCartCache();
      const cart = await getCart(true);
      const { changed } = await this.enforceGiftLimits(cart);

      if (changed) {
        await this.renderSection();
        if (this.progressBar?.update) {
          await this.progressBar.update();
        }
      }
    } catch (error) {
      console.error('Gift cart maintenance error:', error);
    } finally {
      this._giftMaintenanceRunning = false;
    }
  }

  async addUpsellToCart(variantId) {
    if (!variantId) return;

    const button = this.querySelector(`[data-variant-id="${variantId}"]`);
    if (button) {
      window.setButtonLoading(button, true);
    }

    try {
      // Invalidate cache before adding
      invalidateCartCache();
      
      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1
        })
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        // Shopify cart API returns errors in description or message field
        throw new Error(error.description || error.message || 'Failed to add product');
      }

      await this.renderSection();
      this.dispatchEvent(new CustomEvent('cart:item_added', {
        detail: { variantId }
      }));

      // Update progress bar after adding item
      if (this.progressBar && this.progressBar.update) {
        await this.progressBar.update();
      }
    } catch (error) {
      console.error('Error adding upsell to cart:', error);
      alert(error.message || 'Failed to add product to cart');
    } finally {
      if (button) {
        window.setButtonLoading(button, false);
      }
    }
  }
}

customElements.define('cart-drawer', CartDrawer);

// Global fallback: Ensure cart toggle works even if custom element isn't initialized
function initCartToggle() {
  const cartToggle = document.getElementById('cart-toggle');
  const cartDrawerElement = document.querySelector('cart-drawer');
  const drawer = document.getElementById('cart-drawer');
  
  console.log('Cart Drawer Debug:', {
    cartToggle: !!cartToggle,
    cartDrawerElement: !!cartDrawerElement,
    drawer: !!drawer,
    cartDrawerEnabled: cartDrawerElement ? !!cartDrawerElement.querySelector('#cart-drawer') : false,
    sectionId: cartDrawerElement ? cartDrawerElement.dataset.sectionId : 'not found'
  });
  
  // Log individual values for clarity
  console.log('Cart toggle exists:', !!cartToggle);
  console.log('Cart drawer element exists:', !!cartDrawerElement);
  console.log('Drawer #cart-drawer exists:', !!drawer);
  
  // Primary cart-drawer component already handles clicks.
  // Avoid attaching a duplicate fallback listener, which can double-open and double-lock scroll.
  if (cartDrawerElement && typeof cartDrawerElement.open === 'function') {
    return;
  }

  if (cartToggle && !cartToggle.hasAttribute('data-listener-attached')) {
    cartToggle.setAttribute('data-listener-attached', 'true');
    cartToggle.addEventListener('click', (e) => {
      e.preventDefault();
      console.log('Cart toggle clicked');
      
      if (cartDrawerElement && typeof cartDrawerElement.open === 'function') {
        console.log('Opening via cart drawer element');
        cartDrawerElement.open();
      } else if (drawer) {
        console.log('Opening drawer manually');
        // Set hash to #cart before opening
        if (window.location.hash !== '#cart') {
          window.location.hash = '#cart';
        }
        drawer.classList.add('animate', 'active');
        drawer.style.visibility = 'visible';
        drawer.style.zIndex = '999';
        const overlay = document.getElementById('overlay');
        if (overlay) overlay.classList.add('active', 'cart-overlay');
        lockBodyScroll();
        drawer.focus();
      } else {
        console.error('Cart drawer not found!');
      }
    });
  }
}

// Try immediately and on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCartToggle);
} else {
  initCartToggle();
}

// Also try after a short delay in case elements load late
setTimeout(initCartToggle, 500);

/**
 * Quantity Stepper Component
 */
class Stepper extends HTMLElement {
  constructor() {
    super();
    this.dataId = this.dataset.id;
    this.lineKey = this.dataset.lineKey;
    this.stepperInput = this.querySelector('.stepper-input');
    this.stepperIncrement = this.querySelector('.stepper-increment');
    this.stepperDecrement = this.querySelector('.stepper-decrement');
    this.min = parseInt(this.stepperInput?.getAttribute('min') || '0');
    this.max = parseInt(this.stepperInput?.getAttribute('max') || '999');
    this.progressBar = document.querySelector('incentive-progress-bar');
    this.subtotalArr = document.querySelectorAll('.totals__subtotal-value');
    this.cartDrawer = this.closest('cart-drawer');
    
    // Cache SVG strings to avoid repeated string creation
    this.spinnerSVG = '<svg class="icon icon-spinner" viewBox="0 0 32 32" width="16" height="16" fill="none" stroke="#666" xmlns="http://www.w3.org/2000/svg"><use xlink:href="#icon-spinner" /></svg>';
    this.tickSVG = '<svg class="icon icon-tick" viewBox="0 0 32 32" width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg"><path stroke="#286B5A" stroke-linejoin="miter" stroke-linecap="butt" stroke-miterlimit="10" stroke-width="2" d="M24.49 10.34l-11.32 11.32-5.66-5.66"></path></svg>';
    
    this.debouncedOnChange = (window.debounce || function(fn, wait) {
      let timeout;
      return function(...args) {
        clearTimeout(timeout);
        timeout = setTimeout(() => fn.apply(this, args), wait);
      };
    })((event) => {
      this.adjustQty(event);
    }, 300);
  }

  connectedCallback() {
    // debouncedOnChange is already bound in constructor, no need to bind again
    if (this.stepperIncrement) {
      this.stepperIncrement.addEventListener('click', this.debouncedOnChange);
    }
    if (this.stepperDecrement) {
      this.stepperDecrement.addEventListener('click', this.debouncedOnChange);
    }
  }
  
  disconnectedCallback() {
    // Clean up event listeners to prevent memory leaks
    if (this.stepperIncrement) {
      this.stepperIncrement.removeEventListener('click', this.debouncedOnChange);
    }
    if (this.stepperDecrement) {
      this.stepperDecrement.removeEventListener('click', this.debouncedOnChange);
    }
  }

  async adjustQty(event) {
    event.preventDefault();
    if (!this.dataId || !this.stepperInput) return;

    const lineItem = this.closest('.cart__line-item');
    const loadingOverlay = lineItem?.querySelector('.loading-overlay');
    const stepper = this; // The ui-stepper element itself

    // Disable stepper buttons (keep stepper visible for border)
    if (this.stepperIncrement) {
      this.stepperIncrement.disabled = true;
      this.stepperIncrement.style.pointerEvents = 'none';
      this.stepperIncrement.style.opacity = '0.5';
    }
    if (this.stepperDecrement) {
      this.stepperDecrement.disabled = true;
      this.stepperDecrement.style.pointerEvents = 'none';
      this.stepperDecrement.style.opacity = '0.5';
    }

    // Show loading overlay and position it over the stepper
    if (loadingOverlay) {
      // Reset spinner content in case it still has tick icon from previous operation
      const spinnerElement = loadingOverlay.querySelector('.loading-spinner');
      if (spinnerElement) {
        spinnerElement.innerHTML = this.spinnerSVG;
        spinnerElement.classList.remove('showing-tick');
        spinnerElement.style.opacity = '0'; // Start hidden for fade-in
      }
      
      loadingOverlay.classList.remove('hidden');
      
      // Batch getBoundingClientRect calls to minimize layout thrashing
      const stepperRect = stepper.getBoundingClientRect();
      const lineItemRect = lineItem.getBoundingClientRect();
      
      // Batch style updates to minimize reflows
      loadingOverlay.style.cssText = `
        top: ${stepperRect.top - lineItemRect.top}px;
        left: ${stepperRect.left - lineItemRect.left}px;
        width: ${stepperRect.width}px;
        height: ${stepperRect.height}px;
        bottom: auto;
        right: auto;
      `;
      
      // Fade in spinner
      requestAnimationFrame(() => {
        if (spinnerElement) {
          spinnerElement.style.opacity = '1';
        }
      });
    }

    const val = parseInt(this.stepperInput.getAttribute('value') || '0');
    const calcStep = (event.target.dataset.stepValue == 1) ? 1 : -1;
    const newValue = val + calcStep;

    if (newValue === 0) {
      // Remove item
      const removeButton = lineItem?.querySelector('remove-button');
      if (removeButton) {
        removeButton.click();
      }
      // Re-enable stepper
      if (this.stepperIncrement) {
        this.stepperIncrement.disabled = false;
        this.stepperIncrement.style.pointerEvents = '';
        this.stepperIncrement.style.opacity = '';
      }
      if (this.stepperDecrement) {
        this.stepperDecrement.disabled = false;
        this.stepperDecrement.style.pointerEvents = '';
        this.stepperDecrement.style.opacity = '';
      }
      if (loadingOverlay) {
        loadingOverlay.classList.add('hidden');
      }
      return;
    }

    if (newValue < this.min || newValue > this.max) {
      if (loadingOverlay) {
        loadingOverlay.classList.add('hidden');
      }
      // Re-enable stepper
      if (this.stepperIncrement) {
        this.stepperIncrement.disabled = false;
        this.stepperIncrement.style.pointerEvents = '';
        this.stepperIncrement.style.opacity = '';
      }
      if (this.stepperDecrement) {
        this.stepperDecrement.disabled = false;
        this.stepperDecrement.style.pointerEvents = '';
        this.stepperDecrement.style.opacity = '';
      }
      return;
    }

    let success = false;
    try {
      // Invalidate cart cache before updating
      invalidateCartCache();

      const isBundleIncrement = calcStep === 1 && lineItem?.dataset.bundleId;
      const variantId = parseInt(this.dataId, 10);
      if (isBundleIncrement && !Number.isFinite(variantId)) {
        throw new Error('Unable to add item: missing variant id');
      }
      const response = await fetch(
        window.Shopify.routes.root + (isBundleIncrement ? 'cart/add.js' : 'cart/change.js'),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(
            isBundleIncrement
              ? {
                  id: variantId,
                  quantity: 1
                }
              : {
                  id: this.dataset.lineKey || this.dataId,
                  quantity: newValue
                }
          )
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.description || errorData.message || 'Failed to update quantity');
      }

      let cartData;
      if (isBundleIncrement) {
        invalidateCartCache();
        cartData = await getCart(true);
      } else {
        cartData = await response.json();
      }

      if (this.cartDrawer?.enforceGiftLimits) {
        const result = await this.cartDrawer.enforceGiftLimits(cartData);
        cartData = result.cart;
      }

      if (!isBundleIncrement) {
        // Update quantity display - cache query result
        const lineItemArr = document.querySelectorAll(`.line-item-${this.dataId}`);
        if (lineItemArr.length > 0) {
          lineItemArr.forEach((el) => {
            const input = el.querySelector('.stepper-input');
            if (input) {
              input.setAttribute('value', newValue);
            }
          });
        }
      }

      // Update subtotal
      if (this.subtotalArr.length > 0) {
        this.subtotalArr.forEach((el) => {
          el.innerHTML = window.Shopify.formatMoney(cartData.items_subtotal_price);
        });
      }

      // Update progress bar
      if (this.progressBar && this.progressBar.update) {
        this.progressBar.update();
      }

      success = true;
      
      // Show tick icon BEFORE renderSection to ensure it's visible
      // Fade out spinner, then fade in tick
      if (loadingOverlay) {
        const spinnerElement = loadingOverlay.querySelector('.loading-spinner');
        if (spinnerElement) {
          // Fade out spinner
          spinnerElement.style.opacity = '0';
          
          setTimeout(() => {
            // Replace spinner with tick icon (use cached SVG)
            spinnerElement.innerHTML = this.tickSVG;
            spinnerElement.classList.add('showing-tick');
            spinnerElement.style.opacity = '1'; // Fade in tick
            
            // Show tick for a moment, then fade out and render section
            setTimeout(() => {
              // Fade out tick
              spinnerElement.style.opacity = '0';
              
              // Hide overlay and render section, then fade in stepper
              setTimeout(() => {
                // Hide overlay immediately after tick fades out
                if (loadingOverlay) {
                  loadingOverlay.classList.add('hidden');
                  // Reset spinner content for next use (use cached SVG)
                  const resetSpinner = loadingOverlay.querySelector('.loading-spinner');
                  if (resetSpinner) {
                    resetSpinner.innerHTML = this.spinnerSVG;
                    resetSpinner.classList.remove('showing-tick');
                    resetSpinner.style.opacity = '';
                  }
                }
                
                // Render section and fade in stepper
                if (this.cartDrawer) {
                  this.cartDrawer.renderSection().then(() => {
                    // Re-enable stepper buttons (stepper border stays visible)
                    // Cache the line item query to avoid multiple lookups
                    const updatedLineItem = document.querySelector(`.line-item-${this.dataId}`);
                    if (updatedLineItem) {
                      const updatedIncrement = updatedLineItem.querySelector('.stepper-increment');
                      const updatedDecrement = updatedLineItem.querySelector('.stepper-decrement');
                      
                      // Batch style updates
                      if (updatedIncrement) {
                        updatedIncrement.disabled = false;
                        updatedIncrement.style.pointerEvents = '';
                      }
                      if (updatedDecrement) {
                        updatedDecrement.disabled = false;
                        updatedDecrement.style.pointerEvents = '';
                      }
                    }
                  });
                } else {
                  // Fallback if no cartDrawer - fade in buttons
                  if (this.stepperIncrement) {
                    this.stepperIncrement.disabled = false;
                    this.stepperIncrement.style.pointerEvents = '';
                    this.stepperIncrement.style.opacity = '0';
                    requestAnimationFrame(() => {
                      if (this.stepperIncrement) {
                        this.stepperIncrement.style.opacity = '1';
                      }
                    });
                  }
                  if (this.stepperDecrement) {
                    this.stepperDecrement.disabled = false;
                    this.stepperDecrement.style.pointerEvents = '';
                    this.stepperDecrement.style.opacity = '0';
                    requestAnimationFrame(() => {
                      if (this.stepperDecrement) {
                        this.stepperDecrement.style.opacity = '1';
                      }
                    });
                  }
                }
              }, 150); // Fade out tick duration
            }, 400); // Show tick for 400ms
          }, 150); // Fade out spinner duration
        }
      }

      // Dispatch events for sync
      this.dispatchEvent(new CustomEvent('cart:quantity_changed', {
        detail: { lineId: this.dataId, quantity: newValue, giftMaintenanceHandled: true }
      }));
      
      // Also dispatch on document for global listeners (cart page sync)
      document.dispatchEvent(new CustomEvent('cart:quantity_changed', {
        bubbles: true,
        detail: { lineId: this.dataId, quantity: newValue, giftMaintenanceHandled: true }
      }));
    } catch (error) {
      console.error('Error updating quantity:', error);
      // Revert quantity
      this.stepperInput.setAttribute('value', val);
      
      // On error, immediately hide overlay and re-enable
      if (loadingOverlay) {
        loadingOverlay.classList.add('hidden');
      }
      
      if (this.stepperIncrement) {
        this.stepperIncrement.disabled = false;
        this.stepperIncrement.style.pointerEvents = '';
        this.stepperIncrement.style.opacity = '';
      }
      if (this.stepperDecrement) {
        this.stepperDecrement.disabled = false;
        this.stepperDecrement.style.pointerEvents = '';
        this.stepperDecrement.style.opacity = '';
      }
    }
  }
}

customElements.define('ui-stepper', Stepper);

/**
 * Remove Button Component
 */
class RemoveButton extends HTMLElement {
  constructor() {
    super();
    this.dataId = this.dataset.id;
    this.lineKey = this.dataset.lineKey;
    this.subtotalArr = document.querySelectorAll('.totals__subtotal-value');
    this.progressBar = document.querySelector('incentive-progress-bar');
    this.cartDrawer = this.closest('cart-drawer');
    this.debouncedOnRemove = (window.debounce || function(fn, wait) {
      let timeout;
      return function(...args) {
        clearTimeout(timeout);
        timeout = setTimeout(() => fn.apply(this, args), wait);
      };
    })((event) => {
      this.handleRemoveClick(event);
    }, 300);
  }

  connectedCallback() {
    // debouncedOnRemove is already bound in constructor, no need to bind again
    this.addEventListener('click', this.debouncedOnRemove);
  }
  
  disconnectedCallback() {
    // Clean up event listeners to prevent memory leaks
    this.removeEventListener('click', this.debouncedOnRemove);
  }

  async handleRemoveClick(evt) {
    evt.preventDefault();
    if (!this.dataId) return;

    const lineItem = this.closest('.cart__line-item');
    if (!lineItem) return;

    const button = this.querySelector('button');
    const spinner = button?.querySelector('.remove-spinner');
    const icon = button?.querySelector('.remove-icon');

    // Prepare loading state
    if (button) {
      button.classList.add('loading');
      button.disabled = true;
    }
    lineItem.classList.add('loading');
    const initialHeight = lineItem.offsetHeight;
    lineItem.style.height = `${initialHeight}px`;
    lineItem.style.overflow = 'hidden';
    lineItem.style.transition = 'height 0.25s ease, opacity 0.2s ease';

    try {
      // Invalidate cart cache before removing
      invalidateCartCache();
      
      // Target the unique line item key so duplicate-variant bundle lines are
      // removed individually. Fall back to the variant id for non-bundle lines.
      const response = await fetch(window.Shopify.routes.root + 'cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: this.dataset.lineKey || this.dataId,
          quantity: 0
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.description || errorData.message || 'Failed to remove item');
      }

      let cartData = await response.json();

      if (this.cartDrawer?.enforceGiftLimits) {
        const result = await this.cartDrawer.enforceGiftLimits(cartData);
        cartData = result.cart;
      }

      // Update subtotal
      if (this.subtotalArr.length > 0) {
        this.subtotalArr.forEach((el) => {
          el.innerHTML = window.Shopify.formatMoney(cartData.items_subtotal_price);
        });
      }

      // Update progress bar
      if (this.progressBar && this.progressBar.update) {
        this.progressBar.update();
      }

      // Render section if cart is empty or for full update
      if (this.cartDrawer) {
        await this.cartDrawer.renderSection();
      }

      // Update cart icon
      if (this.cartDrawer) {
        await this.cartDrawer.updateCartIcon();
      }

      // Dispatch events for sync
      this.dispatchEvent(new CustomEvent('cart:item_removed', {
        detail: { lineId: this.dataId, giftMaintenanceHandled: true }
      }));
      
      // Also dispatch on document for global listeners (cart page sync)
      document.dispatchEvent(new CustomEvent('cart:item_removed', {
        bubbles: true,
        detail: { lineId: this.dataId, giftMaintenanceHandled: true }
      }));

      // Animate out after successful removal
      const animateOut = () => {
        requestAnimationFrame(() => {
          lineItem.style.opacity = '0';
          lineItem.style.height = '0px';
        });
      };

      const finalizeRemoval = () => {
        lineItem.remove();
      };

      animateOut();
      lineItem.addEventListener('transitionend', finalizeRemoval, { once: true });
      setTimeout(finalizeRemoval, 400); // fallback

      // After animation, re-render cart to refresh totals/items
      setTimeout(async () => {
        if (this.cartDrawer) {
          await this.cartDrawer.renderSection();
          await this.cartDrawer.updateCartIcon();
        }
      }, 250);
    } catch (error) {
      console.error('Error removing item:', error);
      // Restore item on error
      lineItem.classList.remove('loading');
      lineItem.style.opacity = '';
      lineItem.style.height = '';
      lineItem.style.overflow = '';
      lineItem.style.transition = '';
      if (button) {
        button.classList.remove('loading');
        button.disabled = false;
      }
    } finally {
      // Ensure styles reset if element still exists
      if (lineItem && lineItem.parentNode) {
        setTimeout(() => {
          if (lineItem) {
            lineItem.style.overflow = '';
            lineItem.style.transition = '';
          }
        }, 500);
      }
    }
  }
}

customElements.define('remove-button', RemoveButton);

/**
 * Incentive Progress Bar Component
 * Supports multiple incentives configured via Incentives metaobject (e.g., free shipping, free express shipping, free gift)
 */
class IncentiveProgressBar extends HTMLElement {
  constructor() {
    super();
    this.incentives = [];
    this.countryCode = '';
    this.currencyCode = '';
    this.currentIncentives = []; // Incentives with resolved thresholds for current currency
    this.maxThreshold = 0;
    this.initialized = false;
    this.init();
  }

  init(force = false) {
    if (this.initialized && !force) return;

    try {
      // Parse incentives from data attribute
      // Format: [{ label: "free shipping", icon: "shipping-truck", thresholds: { AUD: 15000, USD: 13000, ... } }, ...]
      const incentivesStr = this.dataset.incentives;
      if (incentivesStr) {
        this.incentives = JSON.parse(incentivesStr);
      }

      this.countryCode = this.dataset.country || '';
      
      // Determine currency code
      this.currencyCode = window.Shopify?.currency?.active || 'USD';
      if (this.countryCode) {
        const countryCurrencyMap = {
          'AU': 'AUD', 'US': 'USD', 'GB': 'GBP', 'AE': 'AED',
          'NZ': 'NZD', 'EU': 'EUR', 'CA': 'CAD', 'JP': 'JPY', 'KR': 'KRW'
        };
        this.currencyCode = countryCurrencyMap[this.countryCode] || this.currencyCode;
      }

      // Resolve thresholds for current currency and sort by threshold value
      this.currentIncentives = this.incentives
        .map(incentive => ({
          label: incentive.label,
          unlockedMessage: incentive.unlockedMessage || '',
          icon: incentive.icon || 'shipping-truck',
          threshold: incentive.thresholds[this.currencyCode] || incentive.thresholds['USD'] || 0
        }))
        .filter(incentive => incentive.threshold > 0)
        .sort((a, b) => a.threshold - b.threshold);

      // One marker per threshold — duplicate metaobject tiers stack at 100%.
      const byThreshold = new Map();
      this.currentIncentives.forEach((incentive) => {
        byThreshold.set(incentive.threshold, incentive);
      });
      this.currentIncentives = [...byThreshold.values()].sort(
        (a, b) => a.threshold - b.threshold
      );

      if (this.currentIncentives.length === 0) {
        console.warn('No valid incentives found for currency:', this.currencyCode);
        return;
      }

      this.maxThreshold = this.currentIncentives[this.currentIncentives.length - 1].threshold;
      this.dataset.maxThreshold = this.maxThreshold;
      this.dataset.currentCode = this.currencyCode;

      // Create threshold markers
      this.createThresholdMarkers();

      this.initialized = true;
      this.update();
    } catch (error) {
      console.error('Error initializing incentive bar:', error);
    }
  }

  getIconHTML(iconName) {
    // Return SVG HTML for the specified icon
    return `
      <svg class="icon icon-${iconName}" viewBox="0 0 32 32" width="24" height="24" fill="currentColor" style="color: #000;">
        <use xlink:href="#icon-${iconName}" fill="currentColor" />
      </svg>
    `;
  }

  // Exponent > 1 pushes non-final markers left; final threshold always stays at 100%.
  getThresholdPositionPercent(threshold, isLast) {
    if (isLast) return 100;
    const ratio = threshold / this.maxThreshold;
    if (ratio >= 1) return 92;
    return Math.pow(ratio, 1.4) * 100;
  }

  getProgressRatio(cartTotal) {
    const ratio = Math.min(cartTotal / this.maxThreshold, 1);
    if (ratio >= 1) return 1;
    return Math.pow(ratio, 1.4);
  }

  createThresholdMarkers() {
    const container = this.querySelector('.progress-bar__thresholds');
    if (!container) return;

    container.innerHTML = '';
    this.currentIncentives.forEach((incentive, index) => {
      const marker = document.createElement('div');
      marker.className = 'progress-bar__threshold-marker';
      marker.dataset.threshold = incentive.threshold;
      marker.dataset.label = incentive.label;
      marker.dataset.icon = incentive.icon;
      marker.dataset.index = index;
      
      const isLast = index === this.currentIncentives.length - 1;
      if (isLast) marker.classList.add('progress-bar__threshold-marker--last');
      const percentage = this.getThresholdPositionPercent(incentive.threshold, isLast);
      marker.style.left = `${percentage}%`;

      const circle = document.createElement('div');
      circle.className = 'progress-bar__threshold-circle';
      circle.innerHTML = this.getIconHTML(incentive.icon);
      marker.appendChild(circle);

      const label = document.createElement('div');
      label.className = 'progress-bar__threshold-label';
      label.textContent = window.Shopify?.formatMoney
        ? window.Shopify.formatMoney(incentive.threshold)
        : `$${(incentive.threshold / 100).toFixed(0)}`;
      marker.appendChild(label);

      container.appendChild(marker);
    });
  }

  async update() {
    try {
      // Force a fresh fetch — update() runs after cart mutations, so the cached cart is stale
      const cartData = await getCart(true);
      const max = this.maxThreshold;

      if (!max || !cartData || this.currentIncentives.length === 0) return;

      const cartTotal = cartData.total_price;
      const progressBarFill = this.querySelector('.progress-bar__fill');
      const messageEl = this.querySelector('.shipping-message');
      const container = this;

      const ratio = this.getProgressRatio(cartTotal);

      // Update fill
      if (progressBarFill) {
        progressBarFill.style.transform = `scaleX(${ratio})`;
      }

      // Update threshold markers
      const markers = this.querySelectorAll('.progress-bar__threshold-marker');
      let highestMetIndex = -1;
      
      markers.forEach((marker, index) => {
        const threshold = parseInt(marker.dataset.threshold);
        const isMet = cartTotal >= threshold;
        const svg = marker.querySelector('svg.icon');
        
        if (isMet) {
          marker.classList.add('active');
          if (svg) {
            svg.style.fill = '#fff';
            svg.style.color = '#fff';
            const use = svg.querySelector('use');
            if (use) {
              use.setAttribute('fill', '#fff');
            }
          }
          highestMetIndex = index;
        } else {
          marker.classList.remove('active');
          if (svg) {
            svg.style.fill = 'currentColor';
            svg.style.color = '#000';
            const use = svg.querySelector('use');
            if (use) {
              use.setAttribute('fill', 'currentColor');
            }
          }
        }
      });

      // Update container state and message
      this.updateState(cartTotal, highestMetIndex, messageEl, container);
    } catch (error) {
      console.error('Error updating progress bar:', error);
    }
  }

  formatUnlockedMessage(incentive) {
    if (!incentive) return '';
    if (incentive.unlockedMessage) {
      return incentive.unlockedMessage;
    }
    return `${this.capitalizeFirst(incentive.label)} unlocked!`;
  }

  updateState(cartTotal, highestMetIndex, messageEl, container) {
    if (!messageEl || !container) return;

    const allMet = highestMetIndex === this.currentIncentives.length - 1;
    const firstMet = highestMetIndex >= 0;
    
    // Get the highest achieved incentive and the next target
    const achievedIncentive = firstMet ? this.currentIncentives[highestMetIndex] : null;
    const nextIncentive = firstMet && !allMet 
      ? this.currentIncentives[highestMetIndex + 1] 
      : this.currentIncentives[0];

    // Update container background color
    if (allMet || firstMet) {
      container.classList.add('state-unlocked');
    } else {
      container.classList.remove('state-unlocked');
    }

    // Update message based on state
    let message = '';
    
    if (allMet) {
      message = `
        <div class="shipping-message__unlocked">
          ${this.getCheckmarkIcon()}
          <span>${this.formatUnlockedMessage(achievedIncentive)}</span>
        </div>
      `;
    } else if (firstMet) {
      const remaining = nextIncentive.threshold - cartTotal;
      const remainingFormatted = window.Shopify?.formatMoney
        ? window.Shopify.formatMoney(remaining)
        : `$${(remaining / 100).toFixed(2)}`;
      
      message = `
        <div class="shipping-message__unlocked">
          ${this.getCheckmarkIcon()}
          <span>${this.formatUnlockedMessage(achievedIncentive)}</span>
        </div>
        <div class="shipping-message__next">
          Add <strong>${remainingFormatted}</strong> to unlock <strong>${nextIncentive.label}</strong>.
        </div>
      `;
    } else {
      const remaining = nextIncentive.threshold - cartTotal;
      const remainingFormatted = window.Shopify?.formatMoney
        ? window.Shopify.formatMoney(remaining)
        : `$${(remaining / 100).toFixed(2)}`;
      
      message = `
        <div class="shipping-message__progress">
          Add <strong>${remainingFormatted}</strong> to receive <strong>${nextIncentive.label}!</strong>
        </div>
      `;
    }

    messageEl.innerHTML = message;
  }

  capitalizeFirst(str) {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  getCheckmarkIcon() {
    // Return SVG checkmark icon HTML wrapped in green circle - matching Figma design
    return '<div class="icon-tick-wrapper"><svg class="icon icon-tick" viewBox="0 0 32 32" width="12" height="12" stroke="currentColor" stroke-width="2.5" fill="none"><use xlink:href="#icon-tick" /></svg></div>';
  }
}

customElements.define('incentive-progress-bar', IncentiveProgressBar);

/**
 * Free Gift Drawer — slides over cart from the right
 */
class FreeGiftDrawer extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
  }

  connectedCallback() {
    this.drawer = this.querySelector('#free-gift-drawer') || document.getElementById('free-gift-drawer');
    this.overlay = document.getElementById('overlay');

    this.querySelectorAll('.free-gift-drawer__close').forEach((btn) => {
      btn.addEventListener('click', () => this.close());
    });

    if (this.drawer) {
      this.drawer.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
      });
    }

    if (this.overlay && !this._overlayListenerAttached) {
      this._overlayListenerAttached = true;
      this._onOverlayClick = () => {
        if (this.overlay?.classList.contains('free-gift-overlay')) {
          this.close();
        }
      };
      this.overlay.addEventListener('click', this._onOverlayClick);
    }
  }

  open() {
    if (!this.drawer || this.isOpen) return;

    this.classList.add('active');
    this.drawer.classList.add('animate', 'active');

    if (this.overlay) {
      this.overlay.classList.add('active', 'free-gift-overlay');
      const cartDrawerEl = document.getElementById('cart-drawer');
      if (cartDrawerEl?.classList.contains('active')) {
        this.overlay.classList.add('cart-overlay');
      }
    }

    if (typeof lockBodyScroll === 'function') {
      lockBodyScroll();
    }

    this.isOpen = true;

    const closeBtn = this.drawer.querySelector('.free-gift-drawer__close');
    if (closeBtn) closeBtn.focus();
  }

  close() {
    if (!this.isOpen) return;

    this.classList.remove('active');
    if (this.drawer) {
      this.drawer.classList.remove('active');
    }

    const cartDrawerEl = document.getElementById('cart-drawer');
    const cartStillOpen = cartDrawerEl?.classList.contains('active');

    if (this.overlay) {
      this.overlay.classList.remove('free-gift-overlay');
      if (cartStillOpen) {
        this.overlay.classList.add('active', 'cart-overlay');
      } else {
        this.overlay.classList.remove('active', 'cart-overlay');
        this.overlay.classList.add('overlay-fading');
        setTimeout(() => {
          this.overlay?.classList.remove('overlay-fading');
        }, 300);
        if (typeof unlockBodyScroll === 'function') {
          unlockBodyScroll();
        }
      }
    } else if (!cartStillOpen && typeof unlockBodyScroll === 'function') {
      unlockBodyScroll();
    }

    this.isOpen = false;
  }
}

customElements.define('free-gift-drawer', FreeGiftDrawer);

// Export for global access if needed
window.CartDrawer = CartDrawer;
